import { FC } from "react";

import { Stack } from "@mui/material";
import { isStaticToolUIPart, isTextUIPart, UIMessage } from "ai";

import { Markdown } from "./Markdown/Markdown";
import { getConfiguredToolPartView, getProgressStatus } from "./ToolPart/helpers";
import { ToolPart } from "./ToolPart/ToolPart";
import { UserMessage } from "./UserMessage/UserMessage";

import { AssistantMessageSource, useAssistantConfig } from "../contexts/AssistantConfigContext";
import { localizeAssistantUrls } from "../helpers/localizeAssistantUrls";

import { useTranslate } from "@/common/hooks/useTranslate";
import { AssistantBubble } from "@/components/AssistantBubble/AssistantBubble";
import { AssistantBubbleText } from "@/components/AssistantBubble/AssistantBubbleText";
import { AssistantThinkingIndicator } from "@/components/AIElements/AssistantThinkingIndicator/AssistantThinkingIndicator";

interface ThreadProps {
  messages: UIMessage[];
  isBusy: boolean;
  isComposerDisabled?: boolean;
  onAnswerApproval: (approvalId: string, approved: boolean, note?: string) => void;
  onMessageSent?: (source: AssistantMessageSource) => void;
}

export const Thread: FC<ThreadProps> = ({
  messages,
  isBusy,
  isComposerDisabled = false,
  onAnswerApproval,
  onMessageSent = ignoreMessageSent,
}) => {
  const { t } = useTranslate();
  const config = useAssistantConfig();
  const latestMessage = messages.at(-1);
  const progressStatus =
    latestMessage?.role === "assistant"
      ? getProgressStatus(latestMessage.parts, config.getToolPartDisplay)
      : "thinking";
  const progressLabels = {
    thinking: t("dashboard", "assistant.status.thinking"),
    lookingUp: t("dashboard", "assistant.tool.working"),
  };
  return (
    <Stack
      spacing={3}
      data-test={`${config.feature}-thread`}
      role="log"
      aria-label={t("dashboard", "assistant.title")}
      aria-busy={isBusy}
    >
      {messages.map((message, messageIndex) => {
        if (message.role === "user") {
          return (
            <UserMessage
              key={message.id}
              text={message.parts
                .filter(isTextUIPart)
                .map((part) => part.text)
                .join("\n")}
            />
          );
        }
        const visibleParts = message.parts.filter(
          (part) =>
            (isTextUIPart(part) && part.text) ||
            (isStaticToolUIPart(part) && getConfiguredToolPartView(part, config) !== "none")
        );
        if (visibleParts.length === 0) {
          return null;
        }
        return (
          <AssistantBubble key={message.id}>
            {visibleParts.map((part, index) => {
              if (isTextUIPart(part) && part.text) {
                return (
                  <AssistantBubbleText key={`${message.id}-${index}`}>
                    <Markdown
                      text={
                        message.role === "assistant" && typeof window !== "undefined"
                          ? localizeAssistantUrls(part.text, window.location.origin)
                          : part.text
                      }
                    />
                  </AssistantBubbleText>
                );
              }
              if (isStaticToolUIPart(part)) {
                return (
                  <ToolPart
                    key={part.toolCallId}
                    part={part}
                    reply={messages[messageIndex + 1]}
                    messages={messages}
                    isComposerDisabled={isComposerDisabled}
                    onAnswerApproval={onAnswerApproval}
                    onMessageSent={onMessageSent}
                  />
                );
              }
              return null;
            })}
          </AssistantBubble>
        );
      })}
      {isBusy && <AssistantThinkingIndicator label={progressLabels[progressStatus]} />}
    </Stack>
  );
};

const ignoreMessageSent = () => undefined;
